import React, { useMemo, useState } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import { CardComp, ButtonComp, ModalCompAdvanceUpdate } from "../../../components";
import { TableCompData } from "../../../components/Table";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import { formatMoney, convertToTitleCase } from "../../../utils";
import {
  useGetStaffOnlinePendingPaymentsQuery,
  useGetStaffOnlinePendingPaymentDetailQuery,
  useRequeryStaffOnlinePendingPaymentMutation,
} from "../../../store/admin/onlinePendingPaymentsSlice";

function statusBadge(status) {
  const s = String(status || "").toUpperCase();
  let bg = "#A1A0A0";
  if (s === "PENDING") bg = "#ff9500";
  if (s === "SUCCESSFUL") bg = "#19bc31";
  if (s === "FAILED") bg = "#ff4343";
  return (
    <span
      className="rounded-pill text-white px-2 py-1 small"
      style={{ backgroundColor: bg, whiteSpace: "nowrap" }}
    >
      {convertToTitleCase(String(status || "").replace(/_/g, " "))}
    </span>
  );
}

function paymentKindFromReference(reference) {
  const ref = String(reference || "");
  if (ref.startsWith("PAYMENT_LOAN_")) return "Loan repayment";
  if (ref.startsWith("PAYMENT_ATT_FINE_")) return "Attendance fine";
  if (ref.startsWith("PAYMENT_CONTRIBUTION_")) return "Contribution";
  if (ref.startsWith("PAYMENT_SIGNUP_") || ref.startsWith("PAYMENT_REGISTRATION_")) {
    return "Registration";
  }
  return "Online payment";
}

function providerLabel(provider) {
  const p = String(provider || "").toLowerCase();
  if (p === "alatpay") return "ALATPay / Wema";
  if (p === "paystack") return "Paystack";
  return convertToTitleCase(p || "Unknown");
}

export default function OnlinePendingPayments() {
  const [statusFilter, setStatusFilter] = useState("pending");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [modalPage, setModalPage] = useState("");
  const [requeryingId, setRequeryingId] = useState(null);
  const [alatpayTxnId, setAlatpayTxnId] = useState("");

  const {
    data: listData,
    isFetching,
    isError,
    error: listError,
    refetch,
  } = useGetStaffOnlinePendingPaymentsQuery({
    status: statusFilter,
    search,
    page,
    limit: 20,
  });

  const listErrorMessage =
    listError?.data?.message ||
    listError?.error ||
    (isError ? "Could not load online pending payments." : null);
  const isStaffAuthError =
    listError?.status === 403 &&
    String(listErrorMessage || "")
      .toLowerCase()
      .includes("staff access");

  const { data: detail, isFetching: detailLoading } =
    useGetStaffOnlinePendingPaymentDetailQuery(detailId, {
      skip: !detailId,
    });

  const [requeryPayment] = useRequeryStaffOnlinePendingPaymentMutation();

  const rows = Array.isArray(listData?.data) ? listData.data : [];
  const pagination = listData || {};

  const handleRequery = async (id, transactionIdOverride) => {
    if (!id) return;

    let txn =
      transactionIdOverride ||
      (String(detail?.payment_provider || "").toLowerCase() === "alatpay"
        ? alatpayTxnId.trim()
        : "") ||
      "";

    const rowProvider =
      String(
        detail?.id === id
          ? detail?.payment_provider
          : rows.find((r) => r.id === id)?.payment_provider || ""
      ).toLowerCase();

    if (rowProvider === "alatpay" && !txn) {
      const pasted = window.prompt(
        "Paste the ALATPay Transaction ID from their dashboard (UUID). Requery cannot match by order reference alone."
      );
      txn = (pasted || "").trim();
      if (!txn) {
        toast.error("ALATPay Transaction ID is required to requery this payment.");
        return;
      }
      setAlatpayTxnId(txn);
    }

    setRequeryingId(id);
    try {
      const result = await requeryPayment({
        id,
        transaction_id: txn || undefined,
      }).unwrap();
      const status = String(result?.status || result?.payment?.status || "").toUpperCase();
      if (result?.processed || status === "SUCCESSFUL") {
        toast.success(result?.message || "Payment verified and credited.");
        setAlatpayTxnId("");
      } else if (status === "FAILED") {
        toast.error(result?.message || "Gateway reported payment failed.");
      } else {
        toast(
          result?.message ||
            "Still pending at the gateway. For ALATPay, paste the Transaction ID from their dashboard and requery again."
        );
      }
      refetch();
    } catch (error) {
      toast.error(
        error?.data?.message || error?.message || "Requery failed. Please try again."
      );
    } finally {
      setRequeryingId(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        name: "S/N",
        width: "70px",
        selector: (row, index) => {
          const perPage = pagination?.per_page || 20;
          const currentPage = pagination?.current_page || page;
          return (
            <span className="text-muted fw-2" style={{ fontSize: "14px" }}>
              {(currentPage - 1) * perPage + index + 1}
            </span>
          );
        },
        sortable: false,
      },
      {
        name: "Reference",
        minWidth: "220px",
        cell: (row) => (
          <div>
            <div className="fw-semibold" style={{ fontSize: "13px", fontFamily: "monospace" }}>
              {row.payment_reference}
            </div>
            <div className="small text-muted">
              {paymentKindFromReference(row.payment_reference)}
            </div>
          </div>
        ),
      },
      {
        name: "Membership Number",
        minWidth: "130px",
        selector: (row) => row.member?.member_no || "—",
      },
      {
        name: "Member",
        minWidth: "160px",
        cell: (row) =>
          row.member
            ? `${row.member.first_name || ""} ${row.member.last_name || ""}`.trim() ||
              row.member.member_no
            : "—",
      },
      {
        name: "Provider",
        minWidth: "120px",
        selector: (row) => providerLabel(row.payment_provider),
      },
      {
        name: "Amount",
        minWidth: "120px",
        selector: (row) =>
          formatMoney(Number(row.total_amount || 0).toFixed(2), true),
      },
      {
        name: "Status",
        minWidth: "110px",
        cell: (row) => statusBadge(row.status),
      },
      {
        name: "Started",
        minWidth: "130px",
        selector: (row) =>
          row.created_at
            ? moment(row.created_at).format("DD-MMM-YYYY HH:mm")
            : "—",
      },
      {
        name: "Action",
        minWidth: "220px",
        cell: (row) => {
          const canRequery =
            String(row.status || "").toUpperCase() === "PENDING" ||
            String(row.status || "").toUpperCase() === "FAILED";
          return (
            <div className="d-flex gap-2 flex-wrap">
              <ButtonComp
                type="button"
                btnText="View"
                btnClassName="border border-primary1 text-primary1 px-3 py-1 rounded bg-white"
                ButtonInlineStyle={{
                  whiteSpace: "nowrap",
                  minWidth: "fit-content",
                  fontSize: "13px",
                }}
                onClick={() => {
                  setDetailId(row.id);
                  setModalPage("Detail");
                }}
              />
              {canRequery && (
                <ButtonComp
                  type="button"
                  btnText="Requery"
                  btnClassName="bg-primary1 text-white px-3 py-1 rounded"
                  ButtonInlineStyle={{
                    whiteSpace: "nowrap",
                    minWidth: "fit-content",
                    fontSize: "13px",
                  }}
                  loading={requeryingId === row.id}
                  onClick={() => handleRequery(row.id)}
                />
              )}
            </div>
          );
        },
      },
    ],
    [requeryingId, page, pagination?.current_page, pagination?.per_page]
  );

  const detailModal = [
    {
      name: "Detail",
      component: detailLoading ? (
        <div className="p-4 text-center text-muted">Loading…</div>
      ) : detail ? (
        <div className="p-2">
          <h5 className="fw-bold mb-3">Payment detail</h5>
          <div className="bg-light rounded p-3 mb-3">
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">Reference</span>
              <span style={{ fontFamily: "monospace", fontSize: "13px" }}>
                {detail.payment_reference}
              </span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">Membership Number</span>
              <span>{detail.member?.member_no || "—"}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">Member</span>
              <span>
                {`${detail.member?.first_name || ""} ${
                  detail.member?.last_name || ""
                }`.trim() || "—"}
              </span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">Provider</span>
              <span>{providerLabel(detail.payment_provider)}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">Type</span>
              <span>{paymentKindFromReference(detail.payment_reference)}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">Amount</span>
              <span>
                {formatMoney(Number(detail.total_amount || 0).toFixed(2), true)}
              </span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">Status</span>
              {statusBadge(detail.status)}
            </div>
            <div className="d-flex justify-content-between">
              <span className="text-muted">Started</span>
              <span>
                {detail.created_at
                  ? moment(detail.created_at).format("DD-MMM-YYYY HH:mm")
                  : "—"}
              </span>
            </div>
          </div>
          {(String(detail.status || "").toUpperCase() === "PENDING" ||
            String(detail.status || "").toUpperCase() === "FAILED") && (
            <div className="d-flex flex-column gap-2">
              {String(detail.payment_provider || "").toLowerCase() === "alatpay" && (
                <div>
                  <label className="small text-muted mb-1">
                    ALATPay Transaction ID (required for requery — from their dashboard)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. 9c9ab1aa-a3ce-456d-ac66-1eda2607fdf0"
                    value={alatpayTxnId}
                    onChange={(e) => setAlatpayTxnId(e.target.value)}
                  />
                </div>
              )}
              <ButtonComp
                type="button"
                btnText="Requery gateway"
                btnClassName="bg-primary1 text-white rounded py-2 px-4"
                loading={requeryingId === detail.id}
                onClick={() => handleRequery(detail.id)}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="p-4 text-muted">No details available.</div>
      ),
    },
  ];

  return (
    <div className="px-3 px-lg-4 py-4">
      <CardComp cardClassName="border-0 mb-4">
        <div className="px-4 py-3">
          <h2 className="text-black1 text-xl fw-bold mb-0">Online payment requery</h2>
          <p className="text-muted small mb-0">
            Member Paystack / ALATPay attempts that are still pending appear here.
            Requery the gateway until it returns success or failed — useful when a
            member paid but left the confirmation page before verification finished.
          </p>
        </div>
      </CardComp>

      <CardComp cardClassName="border-0 py-0 px-0">
        <div className="p-3 d-flex flex-wrap gap-2 align-items-center">
          <select
            className="form-select"
            style={{ maxWidth: "200px" }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="successful">Successful</option>
            <option value="all">All</option>
          </select>
          <input
            type="search"
            className="form-control"
            style={{ maxWidth: "280px" }}
            placeholder="Search member or reference..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
          <ButtonComp
            type="button"
            btnText="Refresh"
            btnClassName="border border-secondary2 text-secondary2 px-3 py-2 rounded bg-white"
            onClick={() => refetch()}
          />
        </div>

        {listErrorMessage && (
          <div
            className="mx-3 mb-3 rounded p-3"
            style={{
              backgroundColor: "rgba(255, 67, 67, 0.08)",
              border: "1px solid #ff4343",
              color: "#45464e",
            }}
            role="alert"
          >
            <div className="fw-2 mb-1" style={{ color: "#d40000" }}>
              {isStaffAuthError
                ? "Staff session required"
                : "Failed to load payments"}
            </div>
            <p className="small mb-0">{listErrorMessage}</p>
            {isStaffAuthError && (
              <p className="small mb-0 mt-2 text-muted">
                Your browser is calling staff APIs without a staff token (common
                after testing as a member). Log out, then log in again as Sisi
                Admin and refresh this page.
              </p>
            )}
          </div>
        )}

        <TableCompData
          columns={columns}
          data={isError ? [] : rows}
          progressPending={isFetching}
          progressComponent={<TableSkeletonLoader rows={10} columns={8} />}
        />

        <div className="p-3 d-flex justify-content-between align-items-center">
          <span className="small text-muted">
            Page {pagination.current_page || page} / {pagination.last_page || 1}
          </span>
          <div className="d-flex gap-2">
            <ButtonComp
              type="button"
              btnText="Prev"
              disabled={(pagination.current_page || page) <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            />
            <ButtonComp
              type="button"
              btnText="Next"
              disabled={
                (pagination.current_page || page) >= (pagination.last_page || 1)
              }
              onClick={() => setPage((p) => p + 1)}
            />
          </div>
        </div>
      </CardComp>

      <ModalCompAdvanceUpdate
        size="md"
        pageName={modalPage}
        centered
        arrayComp={detailModal}
        handleClose={() => {
          setModalPage("");
          setDetailId(null);
        }}
      />
    </div>
  );
}
